import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import '../../styles/Auth.css';
import Logo from '../../assets/Logo.png';

const Signup = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check if coming from OTP verification
    const emailFromParams = searchParams.get('email');
    const otpFromParams = searchParams.get('otp');
    const stepFromParams = searchParams.get('step');
    const redirectFrom = searchParams.get('from') || location.state?.from || '/';
    const initialAppliedCoupon = location.state?.appliedCoupon || null;
    
    const [step, setStep] = useState(stepFromParams === 'complete' && emailFromParams && otpFromParams ? 2 : 1);
    const [formData, setFormData] = useState({
        email: emailFromParams || '',
        otp: otpFromParams || '',
        customerName: '',
        phoneNumber: '',
        birthday: '',
        age: '',
        isMarried: '',
        anniversaryDate: '',
        profession: 'Student',
        professionOther: '',
        username: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(initialAppliedCoupon);
    const { signup } = useAuth();

    useEffect(() => {
        // If we have email and OTP from URL params, go to step 2
        if (stepFromParams === 'complete' && emailFromParams && otpFromParams) {
            setStep(2);
            setFormData(prev => ({
                ...prev,
                email: emailFromParams,
                otp: otpFromParams
            }));
        }
    }, [emailFromParams, otpFromParams, stepFromParams]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear anniversary date when marriage status changes to 'no'
        if (name === 'isMarried' && value === 'no') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                anniversaryDate: ''
            }));
        }
        
        // Auto-calculate age when birthday changes
        if (name === 'birthday' && value) {
            const today = new Date();
            const birthDate = new Date(value);
            
            // Check if birth date is in the future
            if (birthDate > today) {
                setErrors(prev => ({
                    ...prev,
                    birthday: 'Birthday cannot be in the future'
                }));
                setFormData(prev => ({
                    ...prev,
                    age: ''
                }));
                return;
            }
            
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            
            // Ensure age is at least 1
            if (age < 1) {
                age = 1;
            }
            
            setFormData(prev => ({
                ...prev,
                age: age.toString()
            }));
            
            // Clear birthday error if age is valid
            if (errors.birthday) {
                setErrors(prev => ({
                    ...prev,
                    birthday: ''
                }));
            }
        }
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        
        // Clear anniversary date error when marriage status changes to 'no'
        if (name === 'isMarried' && value === 'no' && errors.anniversaryDate) {
            setErrors(prev => ({
                ...prev,
                anniversaryDate: ''
            }));
        }
    };

    const validateEmail = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateSignupForm = () => {
        const newErrors = {};

        if (!formData.customerName.trim()) {
            newErrors.customerName = 'Customer name is required';
        } else if (formData.customerName.trim().length < 2) {
            newErrors.customerName = 'Customer name must be at least 2 characters';
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^\+?[\d\s-()]+$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = 'Please enter a valid phone number';
        }

        if (!formData.birthday) {
            newErrors.birthday = 'Birthday is required';
        } else {
            const today = new Date();
            const birthDate = new Date(formData.birthday);
            if (birthDate > today) {
                newErrors.birthday = 'Birthday cannot be in the future';
            }
        }

        if (!formData.age || parseInt(formData.age) < 1 || parseInt(formData.age) > 120) {
            newErrors.age = 'Please enter a valid birthday to calculate age';
        }

        if (!formData.isMarried) {
            newErrors.isMarried = 'Please select marital status';
        }

        // Only require anniversary date if married
        if (formData.isMarried === 'yes' && !formData.anniversaryDate) {
            newErrors.anniversaryDate = 'Anniversary date is required for married customers';
        }

        if (!formData.profession) {
            newErrors.profession = 'Please select a profession';
        }

        if (formData.profession === 'Other' && !formData.professionOther.trim()) {
            newErrors.professionOther = 'Please specify your profession';
        }

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
            newErrors.username = 'Username can only contain letters, numbers, and underscores';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateEmail()) {
            return;
        }

        setLoading(true);
        try {
            await authService.checkEmail(formData.email);
            toast.success('OTP sent to your email!');
            // Redirect to VerifyOTP page with signup type and carry redirect state
            navigate(
                `/verify-otp?email=${encodeURIComponent(formData.email)}&type=signup&from=${encodeURIComponent(redirectFrom)}`,
                { state: { from: redirectFrom, appliedCoupon } }
            );
        } catch (error) {
            toast.error(error.message || 'Failed to send OTP. Please try again.');
            
        } finally {
            setLoading(false);
        }
    };

    const handleSignupSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateSignupForm()) {
            return;
        }

        setLoading(true);
        try {
            const { confirmPassword, ...signupData } = formData;
            
            // Prepare user data, excluding anniversaryDate if not married
            const userData = {
                ...signupData,
                fullName: formData.customerName, // Map customerName to fullName for users table
                otp: formData.otp
            };
            
            // Only include anniversaryDate if user is married and has provided a date
            if (formData.isMarried === 'yes' && formData.anniversaryDate) {
                userData.anniversaryDate = formData.anniversaryDate;
            } else {
                // Remove anniversaryDate from the payload if not married
                delete userData.anniversaryDate;
            }
            
            await signup(userData);
            toast.success('Account created successfully! Welcome to Ginni Fashion Studio!');
            navigate(redirectFrom || '/', { replace: true });
        } catch (error) {
            console.error('Signup error details:', error); // Debug log
            if (error.errors && Array.isArray(error.errors)) {
                const fieldErrors = {};
                error.errors.forEach(err => {
                    fieldErrors[err.path] = err.msg;
                });
                toast.error('Please fix the form errors and try again.');
                setErrors(fieldErrors);
            } else {
                toast.error(error.message || 'Signup failed. Please try again.');
                setErrors({
                    submit: error.message || 'Signup failed. Please try again.'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // Step 1: Email Input
    if (step === 1) {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-logo">
                        <img src={Logo} alt="Ginni Fashion Studio" />
                    </div>
                    
                    <h1 className="auth-title">Create Account</h1>
                    <p className="auth-subtitle">Enter your email to get started</p>

                    <form onSubmit={handleEmailSubmit} autoComplete="off" noValidate>
                        <div className="form-group">
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`form-input ${errors.email ? 'error' : ''}`}
                                placeholder="Email Address"
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                data-lpignore="true"
                                data-form-type="other"
                            />
                            {errors.email && (
                                <div className="error-message">{errors.email}</div>
                            )}
                        </div>

                        {errors.submit && (
                            <div className="error-message" style={{ textAlign: 'center', marginBottom: '15px' }}>
                                {errors.submit}
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="auth-button"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    Sending OTP...
                                </>
                            ) : (
                                'Send OTP'
                            )}
                        </button>
                    </form>

                    <div className="auth-link signup-link">
                        Already have an account? <Link to="/login">Sign in</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Step 2: Complete Signup Form
    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-logo">
                    <img src={Logo} alt="Ginni Fashion Studio" />
                </div>
                
                <h1 className="auth-title">Complete Signup</h1>
                <p className="auth-subtitle">Fill in your details to create your account</p>

                <form onSubmit={handleSignupSubmit} autoComplete="off" noValidate>
                    <div className="form-group">
                        <input
                            type="text"
                            name="customerName"
                            value={formData.customerName}
                            onChange={handleChange}
                            className={`form-input ${errors.customerName ? 'error' : ''}`}
                            placeholder="Full Name"
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.customerName && (
                            <div className="error-message">{errors.customerName}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                            placeholder="Phone Number"
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.phoneNumber && (
                            <div className="error-message">{errors.phoneNumber}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <input
                            type="date"
                            name="birthday"
                            value={formData.birthday}
                            onChange={handleChange}
                            className={`form-input ${errors.birthday ? 'error' : ''}`}
                            placeholder="Birthday"
                            max={new Date().toISOString().split('T')[0]}
                            autoComplete="new-password"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.birthday && (
                            <div className="error-message">{errors.birthday}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <input
                            type="number"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            className={`form-input ${errors.age ? 'error' : ''}`}
                            placeholder="Age (Auto-calculated)"
                            readOnly
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)', cursor: 'not-allowed' }}
                        />
                        {errors.age && (
                            <div className="error-message">{errors.age}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <select
                            name="isMarried"
                            value={formData.isMarried}
                            onChange={handleChange}
                            className={`form-input ${errors.isMarried ? 'error' : ''}`}
                            autoComplete="new-password"
                            data-lpignore="true"
                            data-form-type="other"
                        >
                            <option value="">Are you married?</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                        {errors.isMarried && (
                            <div className="error-message">{errors.isMarried}</div>
                        )}
                    </div>

                    {formData.isMarried === 'yes' && (
                        <div className="form-group">
                            <input
                                type="date"
                                name="anniversaryDate"
                                value={formData.anniversaryDate}
                                onChange={handleChange}
                                className={`form-input ${errors.anniversaryDate ? 'error' : ''}`}
                                placeholder="Anniversary Date"
                                autoComplete="new-password"
                                data-lpignore="true"
                                data-form-type="other"
                            />
                            {errors.anniversaryDate && (
                                <div className="error-message">{errors.anniversaryDate}</div>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <select
                            name="profession"
                            value={formData.profession}
                            onChange={handleChange}
                            className={`form-input ${errors.profession ? 'error' : ''}`}
                            autoComplete="new-password"
                            data-lpignore="true"
                            data-form-type="other"
                        >
                            <option value="Student">Student</option>
                            <option value="Housewife">Housewife</option>
                            <option value="Other">Other</option>
                        </select>
                        {errors.profession && (
                            <div className="error-message">{errors.profession}</div>
                        )}
                    </div>

                    {formData.profession === 'Other' && (
                        <div className="form-group">
                            <input
                                type="text"
                                name="professionOther"
                                value={formData.professionOther}
                                onChange={handleChange}
                                className={`form-input ${errors.professionOther ? 'error' : ''}`}
                                placeholder="Specify your profession"
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                data-lpignore="true"
                                data-form-type="other"
                            />
                            {errors.professionOther && (
                                <div className="error-message">{errors.professionOther}</div>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            className={`form-input ${errors.username ? 'error' : ''}`}
                            placeholder="Username"
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.username && (
                            <div className="error-message">{errors.username}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={`form-input ${errors.password ? 'error' : ''}`}
                            placeholder="Password"
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.password && (
                            <div className="error-message">{errors.password}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                            placeholder="Confirm Password"
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.confirmPassword && (
                            <div className="error-message">{errors.confirmPassword}</div>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="error-message" style={{ textAlign: 'center', marginBottom: '15px' }}>
                            {errors.submit}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="auth-button"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading-spinner"></span>
                                Creating Account...
                            </>
                        ) : (
                            'Create Account'
                        )}
                    </button>
                </form>

                <div className="auth-link signup-link">
                    Already have an account? <Link to="/login">Sign in</Link>
                </div>
            </div>
        </div>
    );
};

export default Signup;